<template>
  <div class="staff-time-modal">
    <NrModal
      :title="modalHeader"
      primaryButtonText="Save"
      showClose
      hasSecondaryButton
      :show="activeModal === 'clock' || activeModal === 'editStaffTime'"
      :disableButtons="disableSubmit"
      @on-primary-click="handleRequestStaffTime"
      @on-close-click="closeRequestStaffTime"
      @on-secondary-click="closeRequestStaffTime"
    >
      <form>
        <NrSelect
          v-if="$can('read', 'publisher_only_items')"
          class="m-top-12"
          v-model="selectedStaff"
          placeholder="Search and select staff"
          :filterable="false"
          :options="filteredStaff"
          :loading="dataFetchState.staff.fetching"
          :hasMore="!dataFetchState.staff.end"
          @input="onStaffSelect"
          @load-more="handleStaffScroll"
          @search="filterStaff"
          @open="filterStaff"
        >
          <template v-slot:label>
            <span>Staff member *</span>
          </template>
        </NrSelect>
        <NrSelect
          :title="dropdownTitle"
          :options="departmentNames"
          v-model="selectedStaffDepartment"
          @input="onDeptInput"
        >
          <template v-slot:label>
            <span>Department</span>
          </template>
        </NrSelect>
        <div class="staff-time-modal-time-fields">
          <NrFormInput
            class="staff-time-modal-hour-field"
            label="Hours"
            type="number"
            name="hours"
            required
            v-model.number="hours"
          />
          <NrFormInput
            class="staff-time-modal-minutes-field"
            label="Minutes"
            type="number"
            name="minutes"
            required
            v-model.number="minutes"
          />
        </div>
        <div
          v-if="
            this.activeModal === 'editStaffTime' &&
            this.staffTime.hourly_rate == null
          "
        >
          <NrFormInput
            label="Hourly Rate (at the time of this work)"
            placeholder="Input dollar amount"
            type="number"
            step="0.01"
            required
            v-model.number="hourlyRate"
          />
          <p class="is-size-7 m-bottom-12">
            The historical hourly rate of this staff time was not stored, but we
            estimate it to have been: {{ formatCurrency(estimatedRate) }}
          </p>
        </div>
        <NrFormLabel for="description"> Description </NrFormLabel>
        <nr-editor
          class="editor-field"
          v-model="description"
          name="description"
          :resizable="true"
          :bold="false"
          :underline="false"
          :italic="false"
          :placeholder="'Describe the work done'"
          :rows="8"
        />
        <!--
        <NrFormLabel>Estimated Total</NrFormLabel>
        <p v-if="estimatedCost && !isNaN(estimatedCost)">
          {{ hours }} hours {{ minutes }} minutes at
          {{ formatCurrency(hourlyRate) }}/hr =
          {{ formatCurrency(estimatedCost) }}
        </p>
        <p v-else-if="!hourlyRate">Error: Hourly rate is not set.</p>
        <p v-else-if="!minutes && !hours">Error: Time is not set.</p>
        -->
      </form>
    </NrModal>
  </div>
</template>

<script>
import API from '../../../vue_shared/api'
import { throttle, isEmpty } from 'lodash'
import { mapActions, mapState } from 'vuex'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'

export default {
  name: 'staff-time-modal',
  components: {
    'nr-editor': NrEditor,
    NrModal,
    NrSelect,
    NrFormLabel,
    NrFormInput
  },
  watch: {
    staffTime() {
      if (this.activeModal === 'editStaffTime') {
        this.description = this.staffTime.description
        this.hours = this.staffTime.hours
        this.minutes = this.staffTime.minutes
        if (this.staffTime.hourly_rate_raw) {
          this.hourlyRate = this.staffTime.hourly_rate_raw
        } else {
          this.hourlyRate = this.estimatedRate
        }
        this.selectedStaff = {
          value: this.staffTime.staff_id,
          label: this.staffTime.staff_name
        }
        this.refreshStaffDeptList(this.staffTime.staff_id).then(() => {
          if (this.staffTime.department_id) {
            this.selectedStaffDepartment = {
              value: this.staffTime.department_id,
              label: this.staffTime.department_name
            }
          }
        })
      }
    }
  },

  props: {
    staffTime: {
      required: false,
      type: Object,
      default() {
        return {}
      }
    }
  },

  data() {
    return {
      selectedStaff: [],
      hours: 0,
      minutes: 0,
      hourlyRate: 0,
      departmentNames: [
        {
          label: 'No departments assigned',
          value: null,
          disabled: true
        }
      ],
      description: '',
      selectedStaffDepartment: {},
      staff: [],
      staffNames: []
    }
  },

  mounted() {
    this.selectedStaff = this.defaultSelectedStaff()
    // Load departments for the current user on mount
    this.refreshStaffDeptList(this.$store.state.currentUser.id)
  },

  computed: {
    ...mapState('request_show', [
      'activeModal',
      'request',
      'requestStaff',
      'dataOptions',
      'dataFetchState',
      'staffDepartments'
    ]),

    filteredStaff() {
      return this.dataOptions.staff.map((staff) => ({
        label: staff.name_with_email,
        value: staff.id
      }))
    },

    modalHeader() {
      return this.activeModal === 'editStaffTime'
        ? 'Edit staff time'
        : 'Add staff time'
    },

    isStaffSelected() {
      return !isEmpty(this.selectedStaff)
    },

    disableSubmit() {
      return (
        !this.isStaffSelected ||
        (!this.hours && !this.minutes) ||
        this.hourlyRate == null
      )
    },

    estimatedCost() {
      if (this.hourlyRate == null) return null
      return this.hourlyRate * (this.hours + this.minutes / 60)
    },

    estimatedRate() {
      return (
        this.staffTime.staff_cost_raw /
        (this.staffTime.hours + this.staffTime.minutes / 60)
      )
    },

    dropdownTitle() {
      return Object.keys(this.selectedStaffDepartment).length
        ? this.selectedStaffDepartment.label
        : 'Select a department'
    }
  },

  methods: {
    ...mapActions('request_show', [
      'setActiveModal',
      'setDisplayCreateStaffTimeBox',
      'fetchDataOptions',
      'submitRequestStaffTime'
    ]),

    formatCurrency(number) {
      return number.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      })
    },

    onDeptInput(val) {
      if (!val) {
        this.selectedStaffDepartment = {}
      }
    },

    closeRequestStaffTime() {
      this.handleCancelRequestStaffTime()
    },

    filterStaff: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'staff' })
    }, 400),

    handleCancelRequestStaffTime() {
      this.$emit('reset-staff-time')
      this.setDisplayCreateStaffTimeBox(false)
      this.selectedStaffDepartment = {}
      this.hours = 0
      this.minutes = 0
      this.selectedStaff = this.defaultSelectedStaff()
      this.description = ''
      this.setActiveModal('')
      this.departmentNames = [
        {
          label: 'No departments assigned',
          value: null,
          disabled: true
        }
      ]
    },

    handleRequestStaffTime() {
      const action = this.staffTime.id ? 'update' : 'create'
      const method = this.staffTime.id ? 'put' : 'post'
      if (!this.isStaffSelected) return
      const payload = {
        action: action,
        method: method,
        user_id: this.selectedStaff.value,
        description: this.description,
        seconds: this.staffTimeInSeconds(),
        request_id: this.request.pretty_id,
        hourly_rate: this.hourlyRate,
        id: this.staffTime ? this.staffTime.id : null
      }
      if (this.selectedStaffDepartment.value)
        payload.department_id = this.selectedStaffDepartment.value
      this.submitRequestStaffTime(payload).then(() => {
        this.handleCancelRequestStaffTime()
      })
    },

    handleStaffScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.staff.search_text,
        dataType: 'staff'
      })
    }, 400),

    onStaffSelect(val) {
      if (!val) {
        this.resetDepartments()
        return
      }
      this.refreshStaffDeptList(val.value)
    },

    resetDepartments() {
      this.departmentNames = [
        {
          label: 'No departments assigned',
          value: null,
          disabled: true
        }
      ]
      this.selectedStaffDepartment = {}
    },

    async refreshStaffDeptList(staffId) {
      try {
        const response = await API.fetchStaffDepartments({
          staff_id: staffId
        })

        if (response.data.length) {
          this.departmentNames = response.data.map((dept) => ({
            label: dept.name,
            value: dept.id
          }))
        } else {
          this.resetDepartments()
        }
        return response
      } catch (error) {
        console.error('Failed to fetch staff departments:', error)
        this.resetDepartments()
      }
    },

    staffTimeInSeconds() {
      return this.hours * 3600 + this.minutes * 60
    },

    defaultSelectedStaff() {
      return {
        value: this.$store.state.currentUser.id,
        label: this.$store.state.currentUser.email_or_name
      }
    }
  }
}
</script>
<style lang="scss">
.editor-field {
  .ql-editor {
    border: 0.5px solid #d1d1d1;
  }
}

.staff-time-modal {
  .nr-modal__overlay {
    z-index: 200;
  }
}

.staff-time-modal-time-fields {
  display: flex;
  .staff-time-modal-hour-field {
    flex: 1;
    margin-right: 1em;
  }
  .staff-time-modal-minutes-field {
    flex: 1;
  }
}
</style>
